import { Link } from "@tanstack/react-router";
import { Phone, Mail, MapPin, Facebook, MessageSquare } from "lucide-react";
import { Logo } from "@/components/Logo";
import sunbrellaLogoAsset from "@/assets/sunbrella-logo.png.asset.json";
const sunbrellaLogo = sunbrellaLogoAsset.url;
import solarfixLogo from "@/assets/solarfix-logo.png";
import ykkLogo from "@/assets/ykk-logo.png";
import { track } from "@/lib/analytics";

const partners: { name: string; url: string; logo?: string; alt: string }[] = [
  {
    name: "Sunbrella® — Glen Raven",
    url: "https://www.sunbrella.com",
    logo: sunbrellaLogo,
    alt: "Sunbrella by Glen Raven — performance outdoor and marine fabric supplier we use for awnings, covers and curtains",
  },
  {
    name: "Solarfix™ Thread",
    url: "https://www.qualitythread.com/products/solarfix-ptfe",
    logo: solarfixLogo,
    alt: "Solarfix PTFE thread — UV-stable lifetime sewing thread we use for outdoor canvas",
  },
  {
    name: "YKK®",
    url: "https://www.ykk.com",
    logo: ykkLogo,
    alt: "YKK — global zipper and fastener manufacturer we use for marine and outdoor canvas hardware",
  },
  {
    name: "Tenara® Thread",
    url: "https://www.tenara.com",
    alt: "Tenara lifetime sewing thread — PTFE thread we use for premium outdoor and marine canvas",
  },
  {
    name: "Strataglass®",
    url: "https://www.strataglass.com",
    alt: "Strataglass — premium scratch-resistant clear vinyl we use for boat enclosures and curtains",
  },
  {
    name: "Serge Ferrari®",
    url: "https://www.sergeferrari.com/en/",
    alt: "Serge Ferrari — high-performance technical membrane fabric we use for tension shade structures",
  },
  {
    name: "Trivantage®",
    url: "https://www.trivantage.com/",
    alt: "Trivantage — wholesale supplier of awning fabrics, hardware and components",
  },
  {
    name: "Keyston Bros.®",
    url: "https://www.keystonbros.com/",
    alt: "Keyston Bros. — distributor of upholstery, marine and awning fabrics and supplies",
  },
  {
    name: "Phifertex®",
    url: "https://www.phifer.com/browse-products/phifertex/",
    alt: "Phifertex by Phifer — vinyl-coated polyester mesh we use for sling chairs and outdoor seating",
  },
  {
    name: "TVF",
    url: "https://www.tvfinc.com/",
    alt: "TVF — Top Value Fabrics, supplier of industrial and outdoor performance textiles",
  },
  {
    name: "Sailrite®",
    url: "https://www.sailrite.com/",
    alt: "Sailrite — supplier of marine and outdoor canvas tools, hardware and sewing equipment",
  },
  {
    name: "Miami Corporation",
    url: "https://miamicorp.com/",
    alt: "Miami Corporation — distributor of awning, marine and outdoor fabrics and hardware",
  },
  {
    name: "Tiger Drylac® Powder Coatings",
    url: "https://www.tiger-coatings.com/us-en/shop/",
    alt: "Tiger Drylac powder coatings — premium powder coat finishes we use on welded aluminum awning and canvas frames",
  },
  {
    name: "Seguin Chamber of Commerce",
    url: "https://www.seguinchamber.com/",
    alt: "Seguin Area Chamber of Commerce — local business affiliation in our hometown of Seguin, Texas",
  },
];

export function SiteFooter({ hidePartners = false }: { hidePartners?: boolean } = {}) {
  return (
    <footer className="bg-white text-navy-deep border-t-4 border-navy-deep">
      {/* Partners & Suppliers */}
      {!hidePartners && (
      <div className="border-b border-navy-deep/15 bg-cream">
        <div className="mx-auto max-w-7xl px-4 py-10 lg:px-8">
          <div className="font-display text-sm tracking-[0.3em] text-navy-deep">★ PARTNERS & SUPPLIERS</div>
          <h3 className="mt-2 font-display text-2xl font-bold text-navy-deep md:text-3xl">
            Trusted Materials & Local Affiliations
          </h3>
          <ul className="mt-6 grid grid-cols-3 gap-2 sm:grid-cols-4 md:grid-cols-6 lg:grid-cols-8">
            {partners.map((p) => (
              <li key={p.url}>
                <a
                  href={p.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  title={p.name}
                  aria-label={`${p.name} (opens in a new tab)`}
                  className="group flex h-14 items-center justify-center gap-2 border border-navy-deep/15 bg-white px-2 py-1.5 transition-all duration-150 motion-reduce:transition-none hover:-translate-y-0.5 hover:border-navy-deep hover:shadow-card focus-visible:-translate-y-0.5 focus-visible:border-navy-deep focus-visible:shadow-card focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-red focus-visible:ring-offset-2 focus-visible:ring-offset-cream"
                >
                  {p.logo ? (
                    <img
                      src={p.logo}
                      alt={p.alt}
                      loading="lazy"
                      className="max-h-7 w-auto max-w-[90%] object-contain opacity-80 transition-opacity duration-150 motion-reduce:transition-none group-hover:opacity-100 group-focus-visible:opacity-100"
                    />
                  ) : (
                    <span className="text-center font-display text-xs uppercase tracking-wide text-navy-deep">
                      {p.name}
                    </span>
                  )}
                </a>
              </li>
            ))}
          </ul>
          <p className="mt-4 text-xs text-navy-deep/70">
            Brand names and logos are property of their respective owners and are shown to identify materials we use. No endorsement is implied.
          </p>

        </div>
      </div>
      )}
      <div className="stripe-bar h-1.5 w-full" />
      <div className="mx-auto max-w-7xl px-4 py-14 lg:px-8">
        <div className="grid gap-10 sm:grid-cols-2 lg:grid-cols-5">
          <div className="sm:col-span-2 lg:col-span-1">
            <Logo className="h-28 w-auto md:h-32 lg:h-40" alt="Texas Made Shade & Covers" loading="lazy" />
            <p className="mt-4 text-sm text-navy-deep/75">
              Custom-built awnings, shade structures, curtains, covers, and aluminum welding — engineered for the Texas heat. Family-owned, custom built Texas for over 30 years.{" "}
              <Link to="/projects" className="font-semibold text-navy-deep underline-offset-2 hover:underline">
                See our Project Gallery →
              </Link>
            </p>
          </div>
          <div>
            <h4 className="font-display text-sm tracking-widest text-navy-deep">Products &amp; Services</h4>
            <ul className="mt-4 space-y-2 text-sm text-navy-deep/80">
              <li><Link to="/awnings" className="hover:text-navy-deep hover:underline">Awnings</Link></li>
              <li><Link to="/commercial-awnings" className="hover:text-navy-deep hover:underline">Commercial Awnings</Link></li>
              <li><Link to="/retractable-awnings" className="hover:text-navy-deep hover:underline">Retractable Awnings</Link></li>
              <li><Link to="/pergola-covers" className="hover:text-navy-deep hover:underline">Patio &amp; Pergola Covers</Link></li>
              <li><Link to="/curtains" className="hover:text-navy-deep hover:underline">Curtains &amp; Panels</Link></li>
              <li><Link to="/sail-shades" className="hover:text-navy-deep hover:underline">Sail Shades &amp; Tension</Link></li>
              <li><Link to="/covers-tops" className="hover:text-navy-deep hover:underline">Covers &amp; Tops</Link></li>
              <li><Link to="/custom-fabrication" className="hover:text-navy-deep hover:underline">Welding &amp; Fabrication</Link></li>
              <li><Link to="/products" className="font-semibold hover:text-navy-deep hover:underline">All Products →</Link></li>
            </ul>
          </div>
          <div>
            <h4 className="font-display text-sm tracking-widest text-navy-deep">Signature Collections</h4>
            <ul className="mt-4 space-y-2 text-sm text-navy-deep/80">
              <li><Link to="/series" className="font-semibold hover:text-navy-deep hover:underline">All Signature Series</Link></li>
              <li><Link to="/alamo-series" className="hover:text-navy-deep hover:underline">Alamo Series™</Link></li>
              <li><Link to="/hill-country-series" className="hover:text-navy-deep hover:underline">Hill Country Series™</Link></li>
              <li><Link to="/longhorn-series" className="hover:text-navy-deep hover:underline">Longhorn Series™</Link></li>
              <li><Link to="/bluebonnet-series" className="hover:text-navy-deep hover:underline">Bluebonnet Series™</Link></li>
              <li><Link to="/rio-grande-series" className="hover:text-navy-deep hover:underline">Rio Grande Series™</Link></li>
            </ul>
          </div>
          <div>
            <h4 className="font-display text-sm tracking-widest text-navy-deep">Service Areas</h4>
            <ul className="mt-4 grid grid-cols-2 gap-x-3 gap-y-1.5 text-sm text-navy-deep/80">
              <li><Link to="/seguin" className="hover:text-navy-deep hover:underline">Seguin</Link></li>
              <li><Link to="/san-antonio" className="hover:text-navy-deep hover:underline">San Antonio</Link></li>
              <li><Link to="/new-braunfels" className="hover:text-navy-deep hover:underline">New Braunfels</Link></li>
              <li><Link to="/san-marcos" className="hover:text-navy-deep hover:underline">San Marcos</Link></li>
              <li><Link to="/austin" className="hover:text-navy-deep hover:underline">Austin</Link></li>
              <li><Link to="/kerrville" className="hover:text-navy-deep hover:underline">Kerrville</Link></li>
              <li><Link to="/boerne" className="hover:text-navy-deep hover:underline">Boerne</Link></li>
              <li><Link to="/fredericksburg" className="hover:text-navy-deep hover:underline">Fredericksburg</Link></li>
              <li><Link to="/uvalde" className="hover:text-navy-deep hover:underline">Uvalde</Link></li>
              <li><Link to="/del-rio" className="hover:text-navy-deep hover:underline">Del Rio</Link></li>
              <li><Link to="/eagle-pass" className="hover:text-navy-deep hover:underline">Eagle Pass</Link></li>
              <li><Link to="/laredo" className="hover:text-navy-deep hover:underline">Laredo</Link></li>
              <li><Link to="/mcallen" className="hover:text-navy-deep hover:underline">McAllen</Link></li>
              <li><Link to="/brownsville" className="hover:text-navy-deep hover:underline">Brownsville</Link></li>
              <li><Link to="/corpus-christi" className="hover:text-navy-deep hover:underline">Corpus Christi</Link></li>
              <li><Link to="/rockport" className="hover:text-navy-deep hover:underline">Rockport</Link></li>
              <li><Link to="/port-aransas" className="hover:text-navy-deep hover:underline">Port Aransas</Link></li>
              <li><Link to="/victoria" className="hover:text-navy-deep hover:underline">Victoria</Link></li>
              <li><Link to="/houston" className="hover:text-navy-deep hover:underline">Houston</Link></li>
            </ul>
          </div>
          <div>
            <h4 className="font-display text-sm tracking-widest text-navy-deep">Contact &amp; Resources</h4>
            <ul className="mt-4 space-y-3 text-sm text-navy-deep/80">
              <li className="flex items-start gap-2">
                <Phone className="h-4 w-4 text-navy-deep mt-0.5 shrink-0" aria-hidden="true" focusable="false" />
                <div className="flex flex-col gap-1">
                  <a
                    href="tel:+18303909588"
                    data-cta-placement="footer"
                    aria-label="Call Texas Made Shade at +1 (830) 390-9588"
                    className="font-semibold text-navy-deep underline-offset-2 hover:underline"
                  >
                    +1 (830) 390-9588
                  </a>
                  <a
                    href="sms:+18303909588"
                    onClick={() => track("sms_click", { target: "8303909588", metadata: { placement: "footer" } })}
                    className="inline-flex items-center gap-1 font-semibold text-navy-deep underline-offset-2 hover:underline"
                    aria-label="Text Texas Made Shade at +1 (830) 390-9588"
                  >
                    <MessageSquare className="h-3.5 w-3.5" aria-hidden="true" focusable="false" /> Text us
                  </a>
                </div>
              </li>
              <li className="flex items-start gap-2">
                <Mail className="h-4 w-4 text-navy-deep mt-0.5 shrink-0" aria-hidden="true" focusable="false" />
                <a
                  href="mailto:office@texasmadeshade.com"
                  onClick={() => track("email_click", { target: "office@texasmadeshade.com", metadata: { placement: "footer" } })}
                  aria-label="Email Texas Made Shade at office@texasmadeshade.com"
                  className="break-words font-semibold text-navy-deep underline-offset-2 hover:underline"
                >
                  office@texasmadeshade.com
                </a>
              </li>
              <li className="flex items-start gap-2"><MapPin className="h-4 w-4 text-navy-deep mt-0.5 shrink-0" aria-hidden="true" /> 959 East Kingsbury Street, Seguin, TX 78155</li>
              <li className="flex items-start gap-2">
                <Facebook className="h-4 w-4 text-navy-deep mt-0.5 shrink-0" aria-hidden="true" />
                <a
                  href="https://www.facebook.com/profile.php?id=61556233022173"
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="See our day-to-day work on Facebook (opens in a new tab)"
                  onClick={() => track("facebook_click", { target: "https://www.facebook.com/profile.php?id=61556233022173", metadata: { placement: "footer" } })}
                  className="font-semibold underline-offset-2 hover:underline"
                >
                  Facebook <span aria-hidden="true">→</span>
                </a>
              </li>
            </ul>
            <ul className="mt-5 space-y-1.5 border-t border-navy-deep/15 pt-4 text-sm text-navy-deep/80">
              <li><Link to="/projects" className="hover:text-navy-deep hover:underline">Project Gallery</Link></li>
              <li><Link to="/case-studies" className="hover:text-navy-deep hover:underline">Case Studies</Link></li>
              <li><Link to="/blog" className="hover:text-navy-deep hover:underline">From the Shop (Blog)</Link></li>
              <li><Link to="/faqs" className="hover:text-navy-deep hover:underline">Awning &amp; Cover FAQs</Link></li>
              <li><Link to="/shop" className="hover:text-navy-deep hover:underline">Shop Hardware &amp; Supplies</Link></li>
              <li><Link to="/accessibility" className="hover:text-navy-deep hover:underline">Accessibility</Link></li>
              <li><Link to="/privacy-policy" className="hover:text-navy-deep hover:underline">Privacy Policy</Link></li>
              <li><Link to="/contact" className="font-semibold hover:text-navy-deep hover:underline">About &amp; Contact →</Link></li>
            </ul>
          </div>
        </div>
        <div className="mt-12 border-t border-navy-deep/15 pt-6 flex flex-col gap-4 sm:flex-row-reverse sm:items-center sm:justify-between">
          {/*
            Pinned sticky CTA (StickyQuoteButton) lives in the bottom-right
            corner. Keep the FAQ pill on the LEFT side (flex-row-reverse on
            sm+) so it never sits behind that floating button on any page.
          */}
          <p className="text-xs text-navy-deep/60 sm:text-right">
            © {new Date().getFullYear()} Texas Made Shade & Covers. Custom Built Texas. All rights reserved.
          </p>
          <Link
            to="/faqs"
            className="group inline-flex items-center gap-2 self-start rounded-full border-2 border-dashed border-rust/60 bg-cream px-4 py-2 font-display text-xs font-bold uppercase tracking-widest text-rust transition hover:border-rust hover:bg-rust hover:text-white sm:self-auto"
          >
            <span className="grid h-5 w-5 place-items-center rounded-full bg-rust text-white transition group-hover:bg-white group-hover:text-rust">
              ?
            </span>
            Got Questions? Read the FAQ
          </Link>
        </div>
        <p className="mt-4 text-xs text-navy-deep/55">
          Solair® is a registered trademark of Glen Raven, Inc. Sunbrella® is a registered trademark of Glen Raven, Inc.
        </p>
      </div>
    </footer>
  );
}
